import { PERMISSIONS } from '@ibe/constants';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Field,
  FieldCheckbox,
} from '@ibe/ui';
import { Plus } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Controller, type UseFormReturn } from 'react-hook-form';

import { PermissionGuard } from '../../../auth/permission-guard.js';
import { useAppForm } from '../../../lib/forms/use-app-form.js';
import {
  AMENITY_DEFAULTS,
  amenities,
  amenityFormInput,
  AmenityFields,
  type AmenityFormInput,
} from '../../amenities/index.js';
import { type RoomTypeFormInput, type RoomTypeAmenityOption } from '../api/schemas.js';

interface RoomTypeAmenitiesFieldsProps {
  readonly form: UseFormReturn<RoomTypeFormInput>;
  readonly amenities: readonly RoomTypeAmenityOption[];
}

interface CreateRoomAmenityDialogProps {
  readonly children: React.ReactNode;
  readonly onCreated: (id: string, name: string) => void;
}

function CreateRoomAmenityDialog({
  children,
  onCreated,
}: CreateRoomAmenityDialogProps): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const create = amenities.useCreate();

  const form = useAppForm<AmenityFormInput>({
    schema: amenityFormInput,
    defaultValues: { ...AMENITY_DEFAULTS, type: 'ROOM' },
    successMessage: 'Amenity created',
    onSubmit: async (values) => {
      const result = await create.mutateAsync({ ...values, iconUrl: values.iconUrl || null });
      onCreated(result.id, values.name);
    },
    onSuccess: () => {
      setOpen(false);
    },
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>New room amenity</DialogTitle>
          <DialogDescription>Add a room amenity to use for this room type.</DialogDescription>
        </DialogHeader>
        <div className="space-y-6">
          <AmenityFields
            register={form.register}
            errors={form.formState.errors}
            control={form.control}
            lockedType="ROOM"
          />
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              disabled={form.submitting}
              onClick={() => {
                setOpen(false);
              }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              disabled={form.submitting}
              onClick={() => {
                void form.submit();
              }}
            >
              {form.submitting ? 'Creating…' : 'Create amenity'}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function dedupeAmenities(
  base: readonly RoomTypeAmenityOption[],
  additions: readonly RoomTypeAmenityOption[],
): readonly RoomTypeAmenityOption[] {
  const seen = new Set(base.map((amenity) => amenity.amenityId));
  return [...base, ...additions.filter((amenity) => !seen.has(amenity.amenityId))];
}

export function RoomTypeAmenitiesFields({
  form,
  amenities: initialAmenities,
}: RoomTypeAmenitiesFieldsProps): React.JSX.Element {
  const { control, watch } = form;
  const [amenityAdditions, setAmenityAdditions] = useState<readonly RoomTypeAmenityOption[]>([]);

  const amenityList = useMemo(
    () => dedupeAmenities(initialAmenities, amenityAdditions),
    [initialAmenities, amenityAdditions],
  );

  const onAmenityCreated = (id: string, name: string): void => {
    const option: RoomTypeAmenityOption = {
      amenityId: id,
      name,
      isFree: true,
      isSelected: true,
    };

    setAmenityAdditions((prev) => [...prev, option]);
    form.setValue('selectedAmenities', {
      ...form.getValues('selectedAmenities'),
      [id]: true,
    });
    form.setValue('amenityFree', {
      ...form.getValues('amenityFree'),
      [id]: true,
    });
  };

  return (
    <section className="space-y-4 rounded-xl border border-border bg-card p-6">
      <div>
        <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
          Section 01
        </p>
        <h2 className="text-lg font-semibold">Room amenities</h2>
        <PermissionGuard permission={PERMISSIONS.AMENITY_CREATE}>
          <CreateRoomAmenityDialog onCreated={onAmenityCreated}>
            <Button variant="outline">
              <Plus className="h-4 w-4" aria-hidden="true" />
              New room amenity
            </Button>
          </CreateRoomAmenityDialog>
        </PermissionGuard>
      </div>

      <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
        {amenityList.map((amenity) => {
          const isSelected = watch(`selectedAmenities.${amenity.amenityId}`);
          return (
            <div
              key={amenity.amenityId}
              className="flex items-center justify-between rounded-md border border-border p-3"
            >
              <Field>
                <Controller
                  control={control}
                  name={`selectedAmenities.${amenity.amenityId}`}
                  defaultValue={false}
                  render={({ field }) => (
                    <FieldCheckbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      onBlur={field.onBlur}
                    >
                      {amenity.name}
                    </FieldCheckbox>
                  )}
                />
              </Field>

              {isSelected ? (
                <Field>
                  <Controller
                    control={control}
                    name={`amenityFree.${amenity.amenityId}`}
                    defaultValue={true}
                    render={({ field }) => (
                      <FieldCheckbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        onBlur={field.onBlur}
                      >
                        Free
                      </FieldCheckbox>
                    )}
                  />
                </Field>
              ) : null}
            </div>
          );
        })}
      </div>
    </section>
  );
}
